import { PnlReportStatus, PnlReportSummary } from '../starGridTypes';

/** UI pill palette classes — resolved by the report-list SCSS. */
export type ReportStatusPill = 'total' | 'ready' | 'generating' | 'failed';

/** Normalize whatever the API returned (string or composite) to a known status bucket. */
export function normalizeReportStatus(raw: string | undefined | null): PnlReportStatus {
  const s = (raw ?? '').toUpperCase();
  if (s === 'READY' || s === 'GENERATING' || s === 'FAILED' || s === 'DELETED') return s;
  // Unknown values (legacy rows, transient states) render as GENERATING so the UI keeps polling.
  return 'GENERATING';
}

/** Human label for a status pill (title case). */
export function reportStatusLabel(status: PnlReportStatus): string {
  switch (status) {
    case 'READY':
      return 'Ready';
    case 'GENERATING':
      return 'Generating…';
    case 'FAILED':
      return 'Failed';
    case 'DELETED':
      return 'Deleted';
  }
}

/** Aggregate counts for the top-of-list status pill row. */
export interface ReportStatusCounts {
  total: number;
  ready: number;
  generating: number;
  failed: number;
}

export function computeStatusCounts(reports: readonly PnlReportSummary[]): ReportStatusCounts {
  const counts: ReportStatusCounts = { total: 0, ready: 0, generating: 0, failed: 0 };
  for (const r of reports) {
    if (normalizeReportStatus(r.status) === 'DELETED') continue;
    counts.total += 1;
    const s = normalizeReportStatus(r.status);
    if (s === 'READY') counts.ready += 1;
    else if (s === 'GENERATING') counts.generating += 1;
    else if (s === 'FAILED') counts.failed += 1;
  }
  return counts;
}

/** Human "5d ago" / "3h ago" — good enough for a card timestamp, avoids a moment dependency. */
export function relativeTime(iso: string | null | undefined, now: Date = new Date()): string {
  if (!iso) return '—';
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return '—';
  const seconds = Math.max(0, Math.floor((now.getTime() - then.getTime()) / 1000));
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  const years = Math.floor(days / 365);
  return `${years}y ago`;
}

/**
 * Compact human-friendly duration:
 *   `< 1 s`   → "215 ms"
 *   `< 60 s`  → "6.14 s"
 *   `< 1 h`   → "22 min 43 s"
 *   `≥ 1 h`   → "1 h 22 min"
 * Used on report/sync UI where long-running values like "1362.64 s" would be
 * hard to eyeball. Falls back to em-dash for null/undefined.
 */
export function formatDurationMs(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  const totalSeconds = ms / 1000;
  if (totalSeconds < 60) return `${totalSeconds.toFixed(2)} s`;
  const totalMinutes = Math.floor(totalSeconds / 60);
  if (totalMinutes < 60) {
    const remSeconds = Math.round(totalSeconds - totalMinutes * 60);
    return remSeconds === 0 ? `${totalMinutes} min` : `${totalMinutes} min ${remSeconds} s`;
  }
  const hours = Math.floor(totalMinutes / 60);
  const remMinutes = totalMinutes - hours * 60;
  return remMinutes === 0 ? `${hours} h` : `${hours} h ${remMinutes} min`;
}

/** Format an integer row count with locale separators, or an em-dash for null. */
export function formatRowCount(n: number | null | undefined): string {
  if (n === null || n === undefined) return '—';
  return n.toLocaleString();
}
